// FILE: src/pages/EditZReportExpectedPage.jsx

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../api";
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Autocomplete
} from "@mui/material";

function EditZReportExpectedPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  // Local state for the expected line record
  const [line, setLine] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Dropdown options
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);

  // 1) Combined data fetching in a single useEffect
  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        // 1. Fetch the ZReport line by ID
        const lineRes = await API.get(`/zreports/${id}`);
        const fetchedLine = lineRes.data;

        // 2. Fetch accounts, categories, users in parallel
        const [resAcc, resCat, resUsers] = await Promise.all([
          API.get("/accounts?limit=100000"),
          API.get("/categories?limit=100000"),
          API.get("/users?limit=1000000")
        ]);

        const accData = resAcc.data.accounts || [];
        const catData = resCat.data || [];
        const userData = resUsers.data || [];

        // Filter categories to only type="C2B"
        const c2bCategories = catData.filter(cat => cat.type === "C2B");

        setLine(fetchedLine);
        setAccounts(accData);
        setCategories(c2bCategories);
        setUsers(userData);
      } catch (err) {
        console.error("Failed to load data:", err);
        setError("Failed to load expected line or dropdown data.");
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [id]);

  // 2) Handler to update fields (Target Cash, Declared Cash => recalc)
  const handleFieldChange = (field, value) => {
    if (!line) return;
    const newLine = { ...line, [field]: value };

    // Recompute if targetCash or declaredCash changed
    if (field === "targetCash" || field === "declaredCash") {
      const target = Number(newLine.targetCash) || 0;
      const declared = Number(newLine.declaredCash) || 0;
      newLine.tillVariance = target - declared;
      newLine.expectedAmount = declared - 150;
    }
    setLine(newLine);
  };

  // 3) Save the updated record via PATCH /zreports/:id
  const handleSave = async () => {
    if (!line) return;
    if (line.status !== "pending") {
      alert("Only pending expected lines can be edited.");
      return;
    }
    try {
      const payload = {
        account: line.account, // the newly chosen account _id
        expectedDate: line.expectedDate,
        category: line.category?._id || line.category, // in case line.category is an object
        targetCash: Number(line.targetCash),
        declaredCash: Number(line.declaredCash),
        tillVariance: Number(line.tillVariance),
        expectedAmount: Number(line.expectedAmount),
        user: line.responsibleUser || line.user || "",
        comments: line.comments || ""
      };
      const res = await API.patch(`/zreports/${line._id}`, payload);
      alert(res.data.message || "Expected line updated successfully.");
      navigate("/zreport-list");
    } catch (err) {
      console.error("Error saving expected line:", err);
      alert(err.response?.data?.message || "Failed to update expected line.");
    }
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4 }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }
  if (!line) {
    return (
      <Container sx={{ mt: 4 }}>
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Typography>No data found.</Typography>
        )}
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Edit Z Report Expected Line
        </Typography>
        {error && <Alert severity="error">{error}</Alert>}

        <Grid container spacing={2} alignItems="center">
          {/* Account (display only) */}
          <Grid item xs={12} sm={4}>
          <Autocomplete
  options={accounts}
  getOptionLabel={(option) =>
    `${option.accountName} (${option.accountNo})`
  }
  value={
    line.account
      ? accounts.find((acc) =>
          typeof line.account === "string"
            ? acc._id === line.account
            : acc._id === line.account._id
        ) || null
      : null
  }
  onChange={(event, newValue) => {
    // Store just the account _id or the whole object if you prefer.
    // Here we store only the _id:
    handleFieldChange("account", newValue ? newValue._id : "");
  }}
  renderInput={(params) => (
    <TextField
      {...params}
      label="Account"
      variant="outlined"
      fullWidth
    />
  )}
  ListboxProps={{ style: { maxHeight: 200 } }}
/>

          </Grid>

          {/* Expected Date */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="Expected Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={line.expectedDate ? line.expectedDate.split("T")[0] : ""}
              onChange={(e) => handleFieldChange("expectedDate", e.target.value)}
            />
          </Grid>

          {/* Category */}
          <Grid item xs={12} sm={4}>
            <Autocomplete
              options={categories}
              getOptionLabel={(option) => option.name}
              // line.category might be an object. If so, match by _id
              value={
                line.category && line.category._id
                  ? categories.find((cat) => cat._id === line.category._id) || null
                  : null
              }
              onChange={(event, newValue) => {
                handleFieldChange("category", newValue ? newValue : "");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Category"
                  variant="outlined"
                  fullWidth
                />
              )}
              ListboxProps={{ style: { maxHeight: 200 } }}
            />
          </Grid>

          {/* All new fields in one row */}
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <TextField
                label="Target Cash"
                type="number"
                required
                value={line.targetCash || ""}
                onChange={(e) => handleFieldChange("targetCash", e.target.value)}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Declared Cash"
                type="number"
                required
                value={line.declaredCash || ""}
                onChange={(e) => handleFieldChange("declaredCash", e.target.value)}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Till Variance"
                type="number"
                value={line.tillVariance || ""}
                InputProps={{ readOnly: true }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Expected Amount"
                type="number"
                value={line.expectedAmount || ""}
                InputProps={{ readOnly: true }}
                sx={{ flex: 1 }}
              />
              <FormControl fullWidth sx={{ flex: 1 }}>
                <InputLabel>User (optional)</InputLabel>
                <Select
                  label="User (optional)"
                  value={line.responsibleUser || line.user || ""}
                  onChange={(e) => handleFieldChange("user", e.target.value)}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {users.map((u) => (
                    <MenuItem key={u._id} value={u._id}>
                      {u.displayName || u.username}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>

          {/* Comments */}
          <Grid item xs={12}>
            <TextField
              label="Comments"
              fullWidth
              multiline
              minRows={2}
              value={line.comments || ""}
              onChange={(e) => handleFieldChange("comments", e.target.value)}
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, textAlign: "right" }}>
          <Button variant="contained" onClick={handleSave}>
            Save Changes
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}

export default EditZReportExpectedPage;
